import { useState, Fragment } from "react";
import { CaretRight, CaretDown } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { ConfirmationDetailTable, WipBadge } from "./ConfirmationDetailTable";
import { formatQty, formatUnitTotals } from "../lib/confirmedProduction";

const formatAt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN") : "—");
const COLUMNS = ["", "Output Product", "Lot ID", "Operation ID", "Operation Name", "Site", "Model ID", "Confirmations", "Total Qty", "UOM", "Open Qty", "Total Scrap", "Total By-product", "Latest Confirmation", "WIP Clearing"];

// Oct 22 2026, user's explicit ask: repeated confirmations of the SAME
// operation on the SAME lot collapse into one row carrying their total and
// the count ("Lot 76687 OP_10: 5 + 2 = 7 EA, 2 confirmations"), and clicking
// it opens every individual transaction behind that total.
export const OperationSummaryTable = ({ groups }) => {
  const [openKey, setOpenKey] = useState(null);
  return (
    <div className="border border-[#D0D5DD] rounded-sm overflow-auto bg-white">
      <table className="w-full text-[13px] border-collapse" data-testid="operation-summary-table">
        <thead>
          <tr>
            {COLUMNS.map((h, i) => (
              <th key={h || i} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase tracking-wide whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g, i) => (
            <Fragment key={g.key}>
              <tr
                className={`cursor-pointer hover:bg-[#F2F4F7] ${i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"}`}
                onClick={() => setOpenKey(openKey === g.key ? null : g.key)}
                data-testid={`operation-row-${g.production_lot_id}-${g.operationId}`}
              >
                <td className="border border-[#D0D5DD] px-1.5 py-1.5 text-[#667085]">
                  {openKey === g.key ? <CaretDown size={13} weight="bold" /> : <CaretRight size={13} weight="bold" />}
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 font-mono">{g.main_output_product || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium text-[#101828]">{g.production_lot_id}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap">
                  {g.operationId}
                  {g.isFinal && <span className="ml-1 text-[10px] font-bold text-[#0E7C86]">(END)</span>}
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{g.operationName}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{g.site_id || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{g.production_model_id || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right">
                  {g.confirmations > 1 ? (
                    <Badge variant="outline" className="bg-[#FFFAEB] text-[#B54708] border-[#FEDF89] tabular-nums" data-testid={`operation-count-${g.production_lot_id}-${g.operationId}`}>
                      {g.confirmations}×
                    </Badge>
                  ) : (
                    <span className="tabular-nums text-[#475467]">1</span>
                  )}
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums font-bold" data-testid={`operation-qty-${g.production_lot_id}-${g.operationId}`}>{formatQty(g.quantity)}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{g.uom}</td>
                <td
                  className={`border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums ${g.openQuantity ? "font-bold text-[#B54708]" : "text-[#667085]"}`}
                  data-testid={`operation-open-qty-${g.production_lot_id}-${g.operationId}`}
                >
                  {g.openQuantity == null ? "—" : formatQty(g.openQuantity)}
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(g.scrap)}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatUnitTotals(g.byproduct)}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap text-[#475467]">{formatAt(g.latestAt)}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5"><WipBadge wip={g.wipClearing} /></td>
              </tr>
              {openKey === g.key && (
                <tr data-testid={`operation-detail-${g.production_lot_id}-${g.operationId}`}>
                  <td colSpan={COLUMNS.length} className="border border-[#D0D5DD] bg-[#F9FAFB] p-3">
                    <ConfirmationDetailTable transactions={g.transactions} testIdPrefix={`operation-${g.production_lot_id}-${g.operationId}`} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          {groups.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length} className="border border-[#D0D5DD] py-8 text-center text-[13px] text-[#667085]" data-testid="operation-summary-empty-state">
                No confirmations match these filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
